import { useContext, useEffect, useMemo, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate } from "react-router-dom";
import { Button, IconGoogle } from "../../../components/atoms";
import {
  Alert,
  FlatpickerGroup,
  InputGroup,
  SelectGroup,
} from "../../../components/molecules";
import {
  Dialog,
  InformPassengers,
  TuorList,
} from "../../../components/organisms";
import { geraVoucher } from "../../../components/organisms/TableReservas";
import { AuthContext } from "../../../contexts/Auth/AuthContext";
import { PdfViewerContext } from "../../../contexts/PdfViewer/InterceptorContext";
import { useApi } from "../../../hooks/useApi";
import { useReservation } from "../../../hooks/useReservation";
import {
  PhoneContainer,
  Wrapper,
  WrapperInformPassengers,
  WrapperTuorList,
} from "./styles";

export function Reservation() {
  const { applicationData } = useContext(AuthContext);
  const { setPdf } = useContext(PdfViewerContext);
  const navigate = useNavigate();
  const api = useApi();

  const [phoneErrors, setPhoneErrors] = useState<Record<number, boolean>>({});
  const possuiTelefoneInvalido = Object.values(phoneErrors).some(
    (erro) => erro,
  );
  const [childrenErrorMessage, setChildrenErrorMessage] =
    useState<boolean>(false);
  const [clientErrorMessage, setClientErrorMessage] = useState<boolean>(false);
  const [adultErrorMessage, setAdultErrorMessage] = useState<boolean>(false);
  const [capacityErrorMessage, setCapacityErrorMessage] = useState<string>("");

  const [openDialog, setOpenDialog] = useState(false);

  const {
    origin,
    setOrigin,
    destiny,
    setDestiny,
    client,
    setClient,
    going,
    setGoing,
    comeBack,
    setComeBack,
    amountOfAdults,
    setAmountOfAdults,
    amountOfChildren,
    setAmountOfChildren,
    goingData,
    setGoingData,
    comeBackData,
    setComeBackData,
    choiceGoing,
    setChoiceGoing,
    choiceComeBack,
    setChoiceComeBack,
    enableButton,
    setEnableButton,
    passengers,
    setPassengers,

    handleSeekTour,
    generateTourTitle,
    updatesAdultPassengerData,
    updatesChildrenPassengerData,
    updatesAllOfThePassengers,
    thereIsATour,
    setThereIsATour,

    resetReservation,
  } = useReservation(applicationData, api);

  const selectedGoingTour = useMemo(
    () => goingData?.find((passeio) => passeio.codigo === +choiceGoing),
    [goingData, choiceGoing],
  );

  useEffect(() => {
    setCapacityErrorMessage("");

    if (selectedGoingTour) {
      const tourCapacity = selectedGoingTour.disponivel;
      const totalPassengers = Number(amountOfAdults) + Number(amountOfChildren);

      if (totalPassengers > tourCapacity) {
        setCapacityErrorMessage(
          `O número de passageiros (${totalPassengers}) excede a quantidade de vagas disponíveis, que é de ${tourCapacity} pessoas.`,
        );
      }

      if (totalPassengers > 10) {
        setCapacityErrorMessage(
          `O número total de passageiros (${totalPassengers}) não pode ser maior do que 10 pessoas.`,
        );
      }
    }

    updatesAllOfThePassengers(passengers);

    setEnableButton(
      !(
        Number(amountOfAdults) > 0 &&
        choiceGoing !== "" &&
        capacityErrorMessage === "" &&
        !passengers.adults.reduce(
          (acc, adult) => (!adult.name || !adult.tel ? true : acc),
          false,
        ) &&
        !passengers.children.reduce(
          (acc, child) => (!child.name ? true : acc),
          false,
        ) &&
        !possuiTelefoneInvalido
      ),
    );
  }, [
    amountOfAdults,
    amountOfChildren,
    choiceGoing,
    passengers,
    phoneErrors,
    updatesAllOfThePassengers,
    setEnableButton,
    selectedGoingTour,
    capacityErrorMessage,
  ]);

  const payment = async () => {
    const passeioIda = goingData?.filter(
      (passeio) => passeio.codigo === +choiceGoing,
    )[0];
    const passeioVolta = comeBackData?.filter(
      (passeio) => passeio.codigo === +choiceComeBack,
    )[0];

    let reserva: Reservation = {
      cliente: client,
      redirectURL: window.location.href,
      email: applicationData.username,
      adultos: passengers.adults.map(({ name, tel }) => ({
        nome: name,
        identificacao: tel,
      })),
      criancas: passengers.children.map(({ name }) => ({ nome: name })),
      useradd:
        applicationData.acesso === "funcionario"
          ? applicationData.funcionario
          : 0,
      checkout: applicationData.checkout,
    };

    if (passeioIda) {
      const { codigo, conexao } = passeioIda;

      if (conexao)
        reserva = {
          ...reserva,
          ida: { passeio: codigo, conexao },
        };
      else
        reserva = {
          ...reserva,
          ida: { passeio: codigo },
        };
    }
    if (passeioVolta) {
      const { codigo, conexao } = passeioVolta;

      if (conexao)
        reserva = {
          ...reserva,
          volta: { passeio: codigo, conexao },
        };
      else
        reserva = {
          ...reserva,
          volta: { passeio: codigo },
        };
    }

    try {
      const pdf = await api.reserva(reserva);
      geraVoucher(pdf.Reserva);

      resetReservation();

      if (pdf.type === "application/pdf") {
        setPdf(pdf as unknown as Blob);
        navigate("/recibo");
      }
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const showPassengerForm =
    (Number(amountOfAdults) > 0 || Number(amountOfChildren) > 0) &&
    selectedGoingTour &&
    !capacityErrorMessage;

  return (
    <div>
      <h1>Compra de passagens</h1>
      <div className="row mt-5">
        <div className="col-sm-12 col-lg-3">
          <SelectGroup
            value={origin}
            id="origem"
            label="Origem"
            options={applicationData.destinos.map((origem: Destino) => ({
              text: origem.nome,
              value: origem.codigo,
            }))}
            onChange={(e) => {
              setOrigin(e.target.value);

              if (destiny !== "0") {
                handleSeekTour(setGoingData, going, e.target.value, destiny);

                if (typeof comeBack !== "string")
                  handleSeekTour(
                    setComeBackData,
                    comeBack,
                    destiny,
                    e.target.value,
                  );
              }
            }}
          />
        </div>
        <div className="col-sm-12 col-lg-3">
          <SelectGroup
            value={destiny}
            id="destino"
            label="Destino"
            options={applicationData.destinos.map((destino: Destino) => ({
              text: destino.nome,
              value: destino.codigo,
            }))}
            onChange={(e) => {
              setDestiny(e.target.value);

              if (origin !== "0") {
                handleSeekTour(setGoingData, going, origin, e.target.value);

                if (typeof comeBack !== "string")
                  handleSeekTour(
                    setComeBackData,
                    comeBack,
                    e.target.value,
                    origin,
                  );
              }
            }}
          />
        </div>
        <div className="col-sm-12 col-lg-6">
          <SelectGroup
            value={client}
            id="cliente"
            label="Cliente"
            messageError={clientErrorMessage ? "Campo obrigatório!" : ""}
            readOnly={applicationData.acesso !== "funcionario"}
            options={applicationData.clientes.map((cliente: Destino) => ({
              text: cliente.nome,
              value: cliente.codigo,
            }))}
            onChange={(e) => {
              setClient(e.target.value);

              setClientErrorMessage(e.target.value === "0");
            }}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12 col-lg-3">
          <FlatpickerGroup
            label="Ida"
            id="data-ida"
            value={going}
            options={{ minDate: "today" }}
            onChange={([date]) => {
              setGoing(date);
              setChoiceGoing("");
              setGoingData(null);
              if (origin !== "0" && destiny !== "0")
                handleSeekTour(setGoingData, date, origin, destiny);

              setThereIsATour(true);
            }}
          />
        </div>
        <div className="col-sm-12 col-lg-3">
          <FlatpickerGroup
            className="col-sm-12 col-lg-2"
            label="Volta"
            id="data-volta"
            value={comeBack}
            options={{ minDate: "today" }}
            onChange={([date]) => {
              setComeBack(date);
              setChoiceComeBack("");
              setComeBackData(null);
              handleSeekTour(setComeBackData, date, destiny, origin);
              setThereIsATour(true);
            }}
          />
        </div>
        <div className="col-sm-12 col-lg-3">
          <InputGroup
            className="col-sm-12 col-lg-2"
            label="Adultos (Idade maior que 06 anos)"
            messageError={adultErrorMessage ? "Campo obrigatório!" : ""}
            id="adultos"
            type="number"
            value={amountOfAdults}
            autoComplete="off"
            onChange={(e) => {
              if (Number(e.target.value) >= 0) {
                setAmountOfAdults(e.target.value);
                setAdultErrorMessage(false);
              }
              if (e.target.value === "") setAdultErrorMessage(true);
            }}
            onKeyUp={() =>
              setPassengers({
                ...passengers,
                adults: Array(Number(amountOfAdults)).fill({
                  name: "",
                  tel: "",
                }),
              })
            }
          />
        </div>
        <div className="col-sm-12 col-lg-3">
          <InputGroup
            className="col-sm-12 col-lg-2"
            label="Crianças (Idade menor 06 anos)"
            messageError={
              childrenErrorMessage
                ? "Campo obrigatório! preencha com 0 (zero) caso não haja crianças."
                : ""
            }
            id="criancas"
            type="number"
            value={amountOfChildren!}
            autoComplete="off"
            onChange={(e) => {
              if (Number(e.target.value) >= 0) {
                setAmountOfChildren(e.target.value);
                setChildrenErrorMessage(false);
              }

              if (e.target.value === "") setChildrenErrorMessage(true);
            }}
            onKeyUp={() =>
              setPassengers({
                ...passengers,
                children: Array(Number(amountOfChildren)).fill({
                  name: "",
                }),
              })
            }
          />
        </div>

        {capacityErrorMessage && (
          <Alert appearance="danger" title="LOTAÇÃO EXCEDIDA" className="mt-3">
            <p className="d-flex gap-2 align-items-center">
              <IconGoogle>error</IconGoogle> {capacityErrorMessage}
            </p>
          </Alert>
        )}
      </div>
      <WrapperTuorList>
        {!thereIsATour ? (
          <Alert appearance="danger" title="INFORMAÇÃO IMPORTANTE">
            <p className="d-flex gap-2">
              <span>
                <IconGoogle>info</IconGoogle>
              </span>{" "}
              Não há nenhum passeio para essa data!
            </p>
          </Alert>
        ) : (
          ""
        )}

        <TuorList
          name="ida"
          show={goingData !== null && origin !== "0" && destiny !== "0"}
          value={choiceGoing}
          setValue={(value) => {
            setChoiceGoing(value);
          }}
          title={
            origin !== "0" && destiny !== "0"
              ? `Ida de ${generateTourTitle(
                  applicationData.destinos,
                  origin,
                )} para ${generateTourTitle(applicationData.destinos, destiny)}`
              : ""
          }
          options={goingData}
        ></TuorList>

        <TuorList
          name="volta"
          show={comeBackData !== null && origin !== "0" && destiny !== "0"}
          value={choiceComeBack}
          setValue={(value) => {
            setChoiceComeBack(value);
          }}
          title={
            origin !== "0" && destiny !== "0"
              ? `Volta de ${generateTourTitle(
                  applicationData.destinos,
                  destiny,
                )} para ${generateTourTitle(applicationData.destinos, origin)}`
              : ""
          }
          options={comeBackData}
        ></TuorList>
      </WrapperTuorList>
      <WrapperInformPassengers>
        <InformPassengers
          name="passageiros"
          show={showPassengerForm}
          title="Passageiros"
        >
          {Number(amountOfAdults) > 0 ? (
            <div>
              <h3>
                <IconGoogle className="adult">person</IconGoogle> Adultos
              </h3>
              {passengers.adults.map((_, index) => (
                <div className="row mb-3" key={index}>
                  <div className="col-6">
                    <InputGroup
                      maxLength={100}
                      value={`${passengers.adults[index].name}`}
                      onChange={(e) => {
                        const adults = updatesAdultPassengerData(
                          index,
                          "name",
                          e.target.value,
                        );

                        setPassengers({
                          ...passengers,
                          adults,
                        });
                      }}
                      label={`Passageiro ${index + 1}`}
                    />
                  </div>
                  <div className="col-6">
                    <PhoneContainer>
                      <label>Celular</label>

                      <PhoneInput
                        country="br"
                        value={passengers.adults[index].tel}
                        enableSearch
                        enableLongNumbers={15}
                        countryCodeEditable={false}
                        disableSearchIcon
                        specialLabel=""
                        searchPlaceholder="Procurar país..."
                        searchNotFound="País não encontrado"
                        onChange={(phone, country: any) => {
                          const somenteNumeros = phone.replace(/\D/g, "");

                          let telefoneValido = false;

                          if (country.countryCode === "br") {
                            const telefoneSemPrefixo = somenteNumeros.replace(
                              /^55/,
                              "",
                            );

                            telefoneValido = telefoneSemPrefixo.length === 11;
                          } else {
                            telefoneValido =
                              somenteNumeros.length >= 8 &&
                              somenteNumeros.length <= 15;
                          }

                          setPhoneErrors((current) => ({
                            ...current,
                            [index]: !telefoneValido,
                          }));

                          const adults = updatesAdultPassengerData(
                            index,
                            "tel",
                            somenteNumeros,
                          );

                          setPassengers({
                            ...passengers,
                            adults,
                          });
                        }}
                        inputStyle={{
                          width: "100%",
                          height: "38px",
                          fontSize: "14px",
                        }}
                        containerStyle={{
                          width: "100%",
                          position: "relative",
                          overflow: "visible",
                        }}
                        dropdownStyle={{
                          zIndex: 99999,
                          maxHeight: "300px",
                        }}
                      />

                      {phoneErrors[index] && (
                        <small className="text-danger">
                          Informe um número de celular válido
                        </small>
                      )}
                    </PhoneContainer>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            ""
          )}

          {Number(amountOfChildren!) > 0 ? (
            <div>
              <h3>
                <IconGoogle>child_care</IconGoogle> Crianças
              </h3>
              <div className="row">
                {passengers.children.map((children, index) => (
                  <div className="col-6" key={index}>
                    <InputGroup
                      value={`${passengers.children[index].name}`}
                      onChange={(e) => {
                        const children = updatesChildrenPassengerData(
                          index,
                          "name",
                          e.target.value,
                        );

                        setPassengers({
                          ...passengers,
                          children,
                        });
                      }}
                      label={`Criança ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            ""
          )}
        </InformPassengers>
      </WrapperInformPassengers>
      <Alert appearance="danger" title="ATENÇÃO" className="mt-5">
        <p>
          Em seu retorno <span>IMPORTANTE</span> respeitar Um tempo mínimo de 6
          horas para o horário do vôo. Após a conclusão da compra, o bilhete
          adquirido será enviado para o seu e-mail cadastrado neste sistema e
          torna-se <span>OBRIGATÓRIO</span> a impressão do mesmo para a
          apresentação em nosso guichê no dia do embarque.
        </p>
      </Alert>
      <Wrapper className="row mt-5">
        <Button
          appearance="primary"
          disabled={enableButton}
          onClick={() => {
            //            console.log(typeof client);
            if (client === "0") setClientErrorMessage(true);
            else if (amountOfChildren === "0") setOpenDialog(true);
            else if (!amountOfChildren) setChildrenErrorMessage(true);
            else {
              payment();
            }
          }}
        >
          Confirmar <IconGoogle>local_atm</IconGoogle>
        </Button>
      </Wrapper>
      <Dialog
        open={openDialog}
        title="Confirmação de passageiros"
        description="Confirma que não há crianças para esse passeio?"
        onShow={(action) => {
          setOpenDialog(action === "opened");
        }}
        actions={
          <>
            <Button
              appearance="success"
              autoFocus
              onClick={(e) => {
                setOpenDialog(false);
                payment();
              }}
            >
              Confirmar
            </Button>
          </>
        }
      />
    </div>
  );
}
